import React, { useState, useRef, useEffect } from 'react';
import { verifyTwoFactorApi, resendVerificationCodeApi } from '../services/auth/loginApi';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';

const TwoFactorVerification = ({ email, type = 'login', onVerificationSuccess, onBack }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    // Auto-submit when all digits are entered
    if (newCode.every(digit => digit !== '') && index === 5) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').split('').slice(0, 6);

    const newCode = [...code];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });

    setCode(newCode);

    // Focus the next empty input or last input
    const nextEmptyIndex = newCode.findIndex(digit => digit === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    if (inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex].focus();
    }

    // Auto-submit if all digits are filled
    if (newCode.every(digit => digit !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleSubmit = async (submittedCode = null) => {
    const verificationCode = submittedCode || code.join('');

    if (verificationCode.length !== 6) {
      toast.error(t('Please enter the 6-digit code'));
      return;
    }

    setIsLoading(true);
    try {
      const user = await verifyTwoFactorApi(email, verificationCode, type);
      onVerificationSuccess(user);
    } catch (error) {
      toast.error(t('Invalid verification code'));
      // Clear inputs on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0].focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    try {
      await resendVerificationCodeApi(email, type);
      setCountdown(30);
      toast.success(t('New verification code sent'));
    } catch (error) {
      toast.error(t('Failed to resend code'));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="sign-in-form">
      <h2>{t('Verification Required')}</h2>
      <p>{t('We sent a verification code to your email')}</p>

      <div className="verification-code-inputs mb-4">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="1"
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className="verification-input"
            disabled={isLoading}
          />
        ))}
      </div>

      <button
        onClick={() => handleSubmit()}
        className="tf-button submit w-100 mb-3"
        disabled={isLoading || code.some(digit => digit === '')}
      >
        {isLoading ? <Spin /> : t('Verify Code')}
      </button>

      <div className="text-center">
        <button
          onClick={handleResendCode}
          className="btn btn-link p-0"
          style={{ color: '#5BF3C7', fontSize: '14px', textDecoration: 'none' }}
          disabled={isResending || countdown > 0}
        >
          {isResending ? (
            <Spin size="small" />
          ) : countdown > 0 ? (
            t('Resend code in {{countdown}}s', { countdown })
          ) : (
            t('Resend verification code')
          )}
        </button>
      </div>

      <div className="text-center mt-3">
        <button
          onClick={onBack}
          className="btn btn-link p-0"
          style={{ color: '#5BF3C7', fontSize: '14px', textDecoration: 'none' }}
        >
          {t('Back to login')}
        </button>
      </div>

      <style jsx>{`
        .verification-code-inputs {
          display: flex;
          gap: 10px;
          justify-content: center;
        }
        .verification-input {
          width: 50px;
          height: 50px;
          text-align: center;
          font-size: 18px;
          border: 2px solid #ddd;
          border-radius: 8px;
          background: white;
        }
        .verification-input:focus {
          border-color: #007bff;
          outline: none;
        }
      `}</style>
    </div>
  );
};

export default TwoFactorVerification;